import { Container } from "pixi.js";
import { PianoLayout } from "../../piano/piano-layout";
import { MusicalTimeline } from "../../music/musical-timeline";
import { PianoComponent } from "@/lib/studio/view/components/piano-component";
import { HintSelection } from "@/lib/studio/view/hint-selection";
import { Background } from "./piano-roll/background";
import { MeasureGrid } from "./piano-roll/grid-lines";
import { Measures } from "./piano-roll/measures";
import { MeasureRuler } from "./piano-roll/measure-ruler";
import { RecordMarker, type RecordEdgeKind } from "./piano-roll/record-marker";
import { Notes, NoteEntry, LaneStyle } from "./piano-roll/notes";
import { NowLine } from "./piano-roll/now-line";
import { Playhead } from "./piano-roll/playhead";
import { NoteHints } from "./piano-roll/note-hints";
import { Vignette } from "./piano-roll/vignette";
import { PastOverlay, SOFT_CURVE, STEEP_CURVE } from "./piano-roll/past-overlay";
import { NoteDust } from "./piano-roll/note-dust";
import { PedalRibbon } from "./piano-roll/pedal-ribbon";
import { PedalTrails } from "./piano-roll/pedal-trails";
import { PedalFlash } from "./piano-roll/pedal-flash";
import type { SustainSpan } from "@/lib/track/lane";
import type { PedalTrail } from "./piano-roll/pedal-span";
import { WorkRange } from "./piano-roll/work-range";
import { SongBoundaries } from "./piano-roll/song-boundaries";
import type { SongBoundary } from "@/lib/studio/view/sequence-layout";
import { KeySpans } from "./piano-roll/key-spans";
import type { KeySpan } from "@/lib/harmony/key-spans";
import { Harmony, HintRow } from "./piano-roll/harmony";
import { OctaveGrid } from "./piano-roll/octave-grid";
import { pitchClassColor } from "@/lib/music/pitch-colors";
import { NoteEffectsLayer } from "./piano-roll/note-effects/effects-layer";
import type { NoteEffectFrame } from "./piano-roll/note-effects/types";
import {
  DEFAULT_NOTE_EFFECT_STYLE,
  type ActivePress,
  type NoteEffectStyle,
} from "./piano-roll/note-effects/types";

/**
 * Configuration for the piano roll renderer (rendering-only params).
 */
export type PianoRollConfig = {
  /** Horizontal gap between note rectangles */
  noteGap: number;
  /** Corner radius of note rectangles */
  noteRadius: number;
  /** Ticks per beat (PPQ) */
  ticksPerBeat: number;
  /** Beats per measure (time signature numerator) */
  beatsPerMeasure: number;
};

const DEFAULT_CONFIG: PianoRollConfig = {
  noteGap: 2,
  noteRadius: 3,
  ticksPerBeat: 480,
  beatsPerMeasure: 4,
};

/**
 * Props passed to draw() each frame, computed by PianoRollComponent.
 */
export type PianoRollRendererProps = {
  fromTick: number;
  toTick: number;
  currentTick: number;
  /** Current playhead position in seconds (for tempo-invariant fade) */
  currentSeconds: number;
  /** Tick-to-seconds converter bound to the current musical timeline */
  tickToSeconds: (tick: number) => number;
  /** Y coordinate of the playhead line */
  playheadY: number;
  /**
   * Le délai de restitution, en pixels : de combien la ligne du présent est
   * au-dessus de la tête de lecture. Zéro sans délai.
   *
   * Voir {@link NowLine} : c'est le temps qui sépare le geste du son, rendu
   * visible parce qu'il est autrement déroutant.
   */
  playoutDelayPx?: number;
  /** Pre-filtered, pre-computed note entries from all visible, unmuted lanes */
  entries: NoteEntry[];
  hints: HintRow[];
  /**
   * The key spans of the piece, empty while it stays in its key; see
   * `keySpansOf`. Drawn on the right edge by `KeySpans`.
   */
  keySpans: readonly KeySpan[];
  /** Writes a span's key and its relation to the piece's key, for the player. */
  keyLabelOf: (span: KeySpan) => { key: string; relation: string };
  width: number;
  height: number;
  hintSelection?: HintSelection;
  /** When true, the beat line containing currentTick is rendered in accent style. */
  highlightActiveBeat?: boolean;
  /**
   * Le pointeur est posé sur la règle graduée : elle s'ouvre et écrit ses
   * numéros de mesure. Voir `MeasureRuler`.
   */
  rulerExpanded?: boolean;
  /**
   * Les bornes de la zone d'enregistrement, calées sur des demi-temps, et leur
   * lecture musicale — la mesure qui les contient, le temps dans la mesure, et
   * s'il s'agit du demi-temps. `null` tant que la borne n'est pas posée. Voir
   * `RecordMarker`.
   *
   * Passées à plat, et non en deux objets : le renderer compare ses props par
   * copie de surface, dans laquelle un objet réutilisé d'une image sur l'autre
   * serait comparé à lui-même — le piège documenté sur `previousEntryCount`.
   */
  recordStartTick?: number | null;
  recordStartBar?: number;
  recordStartBeat?: number;
  recordStartOffBeat?: boolean;
  recordEndTick?: number | null;
  recordEndBar?: number;
  recordEndBeat?: number;
  recordEndOffBeat?: boolean;
  /** La borne en cours de déplacement, s'il y en a une. */
  recordDragging?: RecordEdgeKind | null;
  /** À quel point la borne tenue est sortie de la règle, de 0 à 1. */
  recordDiscardProgress?: number;
  /** Abscisse du pointeur qui tient une borne, `null` sinon. */
  recordDragX?: number | null;
  /** Per-laneIdx fill/stroke colors (resolved upstream from LaneView). */
  laneColors: readonly LaneStyle[];
  /**
   * Range currently being worked on, drawn with its outside dimmed and its
   * two boundaries ruled. `null` outside the learning journey.
   */
  workRange?: { startTick: number; endTick: number } | null;
  /**
   * Le calque doit-il régler les bornes de cette plage ?
   *
   * Faux quand la plage est la zone d'enregistrement : c'est `RecordMarker`
   * qui trace ses bornes, et il le fait en violet.
   */
  workRangeRuled?: boolean;
  /**
   * Les frontières entre les morceaux d'une séquence, sur l'axe du rouleau,
   * et le numéro de version de cette liste — c'est lui que la détection de
   * changement compare, la liste étant réutilisée tant que rien ne bouge.
   * Vides sans séquence. Voir `SongBoundaries`.
   */
  songBoundaries: readonly SongBoundary[];
  sequenceVersion: number;
  /**
   * La pédale, en trois lectures de la même chose.
   *
   * `pedalTrails` porte les notes qui sonnent encore : relevées lane par lane
   * — une note n'est tenue que par la pédale de la sienne —, et déjà bornées
   * par l'extinction de leur corde.
   *
   * `pedalWritten` et `pedalPlayed` sont les deux rubans du bord — la
   * partition et toi. `pedalFlash` est l'éclat de ton enfoncement, de 1 à 0.
   */
  pedalTrails: readonly PedalTrail[];
  pedalWritten: readonly SustainSpan[];
  pedalPlayed: readonly SustainSpan[];
  pedalFlash: number;
  /** Strikes crossing the playhead, projected to roll-local positions. */
  presses: readonly ActivePress[];
  /** Wall-clock delta in milliseconds since the last draw. */
  deltaMS: number;
  /**
   * La vitesse du rouleau, en part de sa vitesse nominale : 1 au tempo de
   * référence du morceau, 2 quand il descend deux fois plus vite. C'est
   * l'horloge des effets qui doivent suivre le morceau, voir `rollSpeedRatio`.
   */
  rollSpeedRatio: number;
  /** Shared horizontal offset (pan/centring); a change must trigger a repaint. */
  offsetX: number;
};

/**
 * Pure Pixi.js renderer for the piano roll (falling notes).
 *
 * Receives all data pre-computed by PianoRollComponent, no transport reads,
 * no lane queries. Delegates to Background, Measures, Notes, pastOverlay, Playhead.
 */
export class PianoRollRenderer {
  private config: PianoRollConfig;
  private background: Background;
  private octaveGrid: OctaveGrid;
  private grid: MeasureGrid;
  private measures: Measures;
  private measureRuler: MeasureRuler;
  private recordMarker: RecordMarker;
  /**
   * Les deux bornes telles que `RecordMarker` les lit, remplies à chaque image.
   *
   * Gardées ici plutôt que reconstruites : le repère en prend une copie pour sa
   * propre détection de changement, donc les muter est sans danger, et une
   * allocation par image sur le chemin chaud n'apporterait rien.
   */
  private markerStart = { tick: null as number | null, barNumber: 1, beatNumber: 1, offBeat: false };
  private markerEnd = { tick: null as number | null, barNumber: 1, beatNumber: 1, offBeat: false };
  private notes: Notes;
  private pedalTrails: PedalTrails;
  private pedalRibbon: PedalRibbon;
  private pedalFlash: PedalFlash;
  private harmony: Harmony;
  /** L'étage des lignes d'accord, sous les notes. Voir le constructeur. */
  private harmonyLines: Container;
  private keySpans: KeySpans;
  private pastOverlay: PastOverlay;
  private noteDust: NoteDust;
  /** Les notes partent-elles en paillettes ? Non tant que `setNoteDust` ne dit pas le contraire. */
  private noteDustEnabled = false;
  private workRange: WorkRange;
  private songBoundaries: SongBoundaries;
  private playhead: Playhead;
  private nowLine: NowLine;
  private noteEffects: NoteEffectsLayer;
  /** Réutilisé : cet objet partait à chaque image, avant même le bailout. */
  private readonly effectFrame: NoteEffectFrame = {
    presses: [],
    deltaMS: 0,
    rollSpeedRatio: 1,
    width: 0,
    height: 0,
  };
  private noteHints: NoteHints;
  private vignette: Vignette;

  private previousProps?: PianoRollRendererProps;
  private previousHintVersion = -1;
  /**
   * Number of entries the last painted frame held.
   *
   * Tracked apart from `previousProps` because it cannot be read from there:
   * `previousProps` is a **shallow** copy, and `entries` is a buffer the
   * component reuses and mutates in place, so `previousProps.entries` is the
   * very array being compared against — its length always matches. The test
   * that was meant to catch "the notes changed" therefore never fired, and
   * only survived because `fromTick` moves on almost every frame that also
   * changes the notes. It stopped surviving the moment something added or
   * removed notes on a **parked** roll: the trace appeared on the way in
   * (the park animation moved the window) and would not go away on the way
   * out, because nothing else had moved.
   */
  private previousEntryCount = -1;

  constructor(
    private stage: Container,
    private layout: PianoLayout,
    musicalTimeline: MusicalTimeline,
    private pianoComponent: PianoComponent,
    config?: Partial<PianoRollConfig>
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    this.background = new Background(this.stage);
    this.octaveGrid = new OctaveGrid(this.stage, this.layout, this.pianoComponent);
    // Une seule grille métrique pour les deux vues qui la dessinent : la trame
    // qui traverse le roll, et la règle de son bord gauche.
    this.grid = new MeasureGrid(musicalTimeline);
    this.measures = new Measures(this.stage, this.grid);
    // Les lignes d'accord, sous les notes : elles traversent le roll d'un bord
    // à l'autre et ne doivent barrer aucune note au passage. Leurs étiquettes,
    // elles, restent tout en haut (voir `this.harmony`).
    this.harmonyLines = new Container();
    this.stage.addChild(this.harmonyLines);
    // Sous les notes : la traîne sort de la note et ne la recouvre jamais,
    // mais une corde qui résonne se lit derrière le doigt qui l'a frappée.
    this.pedalTrails = new PedalTrails(this.stage, this.layout, this.pianoComponent);
    this.notes = new Notes(this.stage, this.layout, this.pianoComponent, this.config.noteGap, this.config.noteRadius);
    // Le ruban est un relevé : il vieillit avec la musique, donc il passe
    // sous le voile du passé comme les notes.
    this.pedalRibbon = new PedalRibbon(this.stage);
    // Overlay dims everything below the playhead (past portion). With glitter
    // on (`setNoteDust`), the notes are already gone below the line, so it
    // takes at once.
    this.pastOverlay = new PastOverlay(this.stage, SOFT_CURVE);
    // Ce que la tête de lecture a mangé des notes : des paillettes, au-dessus
    // du voile, pour qu'elles brillent au lieu de s'y éteindre. Éteintes à la
    // création.
    this.noteDust = new NoteDust(this.stage, this.layout, this.pianoComponent);
    // The bounds of the passage being worked on, over the notes and under the
    // playhead: it frames them, it does not compete with the line that says
    // "now".
    // L'éclat, lui, est au présent : au-dessus du voile, sinon l'instant du
    // geste naîtrait déjà à moitié éteint.
    this.pedalFlash = new PedalFlash(this.stage);
    this.workRange = new WorkRange(this.stage);
    // Les frontières d'une séquence, au même étage que les bornes du passage
    // travaillé : sur les notes et sur le voile du passé — une frontière
    // franchie reste un repère —, sous tout ce qui dit l'instant.
    this.songBoundaries = new SongBoundaries(this.stage);
    // La règle passe sur les notes — sinon une basse traverserait ses
    // graduations et son panneau — mais sous tout ce qui dit *maintenant* et
    // sous ce qui porte du texte : la tête de lecture et sa teinte d'accord,
    // les noms d'accords, la marge de tonalité. Une graduation ne gagne contre
    // rien de tout cela ; elle se lit à côté.
    this.measureRuler = new MeasureRuler(this.stage, this.grid);
    // Playhead is added last so it renders on top of notes and overlay
    // Sous le playhead dans l'ordre d'ajout : la ligne du présent indique un
    // horizon, elle ne doit pas passer devant celle qui dit l'instant sonore.
    this.nowLine = new NowLine(this.stage);
    this.playhead = new Playhead(this.stage);
    // Note effects sit between the playhead and the topmost UI layers, so the
    // halo/particles glow over the playhead line and notes.
    this.noteEffects = new NoteEffectsLayer(this.stage, DEFAULT_NOTE_EFFECT_STYLE);
    // NoteHints renders on top of everything including the playhead line
    this.noteHints = new NoteHints(this.stage, this.layout, this.pianoComponent, musicalTimeline);
    // Vignette is the topmost layer
    this.vignette = new Vignette(this.stage);
    // Le repère reste au-dessus de la vignette, à la différence de la règle :
    // ses bornes sont un geste du joueur, pas un fond de plan, et une borne
    // qui s'efface aux bords du roll ne borne plus rien. Sa capsule se pose
    // dans la gouttière, et sa ligne traverse le roll.
    this.recordMarker = new RecordMarker(this.stage);
    this.harmony = new Harmony(this.stage, this.harmonyLines);
    // The tonality margin, on the right edge, over everything: a change of
    // key is a landmark, and the vignette must not dim it.
    this.keySpans = new KeySpans(this.stage);
  }

  setNoteEffectStyle(style: NoteEffectStyle): void {
    this.noteEffects.setStyle(style);
  }

  /**
   * Les notes partent en paillettes en passant la tête de lecture, ou la
   * traversent entières comme avant. Décidé côté serveur (`NOTE_DUST`) et
   * descendu jusqu'ici.
   */
  setMeasureLinesVisible(visible: boolean): void {
    this.measures.setLinesVisible(visible);
    this.invalidate();
  }

  setNoteDust(enabled: boolean): void {
    this.noteDustEnabled = enabled;
    this.noteDust.setEnabled(enabled);
    this.pastOverlay.setCurve(enabled ? STEEP_CURVE : SOFT_CURVE);
    this.invalidate();
  }

  /**
   * Repaint the next frame whatever the props say.
   *
   * For mutations the props cannot express — a lane changing what it *is*
   * rather than what it holds. Cheaper and clearer than threading every such
   * flag through `shouldRedraw`.
   */
  invalidate(): void {
    this.previousProps = undefined;
  }

  hasActiveEffects(): boolean {
    return this.noteEffects.isAnimating() || this.noteDust.isAnimating();
  }

  /**
   * La règle est-elle en train de s'ouvrir ou de se refermer ?
   *
   * Le composant le relaie au gouverneur de rendu : l'ouverture est la seule
   * animation du roll que rien d'autre ne tient éveillée — la souris qui la
   * déclenche s'arrête souvent avant qu'elle ne soit finie.
   */
  isRulerAnimating(): boolean {
    return this.measureRuler.isAnimating();
  }

  setMusicalTimeline(musicalTimeline: MusicalTimeline): void {
    this.grid.setMusicalTimeline(musicalTimeline);
    this.measures.invalidate();
    this.measureRuler.invalidate();
    this.recordMarker.invalidate();
    this.noteHints.setMusicalTimeline(musicalTimeline);
  }

  setConfig(config: Partial<PianoRollConfig>) {
    this.config = { ...this.config, ...config };
  }

  // Width/height are now passed through draw() props; setSize is a no-op kept for API compatibility.
  setSize(_width: number, _height: number): void {}

  draw(props: PianoRollRendererProps): void {
    // Notes' hit highlight depends on which keys are held, so any time presses
    // exist we must rerun the static draw path even if fromTick/etc are stable.
    const shouldRedraw = this.shouldRedraw(props)
      || props.presses.length > 0
      || this.measureRuler.isAnimating();

    // Effects always update so particles can fade out and halos animate
    // even when the rest of the scene is unchanged.
    const effectFrame = this.effectFrame;
    effectFrame.presses = props.presses;
    effectFrame.deltaMS = props.deltaMS;
    effectFrame.rollSpeedRatio = props.rollSpeedRatio;
    effectFrame.width = props.width;
    effectFrame.height = props.height;
    this.noteEffects.update(effectFrame);
    this.noteDust.update(props.deltaMS);

    if (!shouldRedraw) {
      return;
    }

    const {
      fromTick,
      toTick,
      currentTick,
      currentSeconds,
      tickToSeconds,
      playheadY,
      entries,
      hints,
      width,
      height,
    } = props;

    this.background.draw({ width, height });

    this.octaveGrid.draw({ width, height });

    this.measures.draw({
      fromTick,
      toTick,
      width,
      height,
      highlightActive: props.highlightActiveBeat ?? false,
    });

    this.measureRuler.draw({
      fromTick,
      toTick,
      height,
      expanded: props.rulerExpanded ?? false,
      accentBarStart: props.recordStartTick === null || props.recordStartTick === undefined
        ? null
        : props.recordStartBar ?? null,
      accentBarEnd: props.recordEndTick === null || props.recordEndTick === undefined
        ? null
        : props.recordEndBar ?? null,
      deltaMS: props.deltaMS,
    });

    this.markerStart.tick = props.recordStartTick ?? null;
    this.markerStart.barNumber = props.recordStartBar ?? 1;
    this.markerStart.beatNumber = props.recordStartBeat ?? 1;
    this.markerStart.offBeat = props.recordStartOffBeat ?? false;

    this.markerEnd.tick = props.recordEndTick ?? null;
    this.markerEnd.barNumber = props.recordEndBar ?? 1;
    this.markerEnd.beatNumber = props.recordEndBeat ?? 1;
    this.markerEnd.offBeat = props.recordEndOffBeat ?? false;

    this.recordMarker.draw({
      start: this.markerStart,
      end: this.markerEnd,
      fromTick,
      toTick,
      width,
      height,
      rulerWidth: this.measureRuler.panelWidth,
      openness: this.measureRuler.openness,
      dragging: props.recordDragging ?? null,
      discardProgress: props.recordDiscardProgress ?? 0,
      dragX: props.recordDragX ?? null,
    });

    this.pedalTrails.draw({
      trails: props.pedalTrails,
      laneColors: props.laneColors,
      fromTick,
      toTick,
      height,
      noteGap: this.config.noteGap,
      clipY: this.noteDustEnabled ? playheadY : undefined,
    });
    this.notes.draw({
      entries,
      fromTick,
      toTick,
      width,
      height,
      hintSelection: props.hintSelection,
      laneColors: props.laneColors,
      // Glitter on: notes stop at the line and leave as glitter. Off: they
      // pass under it whole, into the overlay.
      clipY: this.noteDustEnabled ? playheadY : undefined,
    });
    this.pedalRibbon.draw({
      written: props.pedalWritten,
      played: props.pedalPlayed,
      fromTick,
      toTick,
      height,
    });
    this.pedalFlash.draw({ width, playheadY, strength: props.pedalFlash });
    this.harmony.draw({ hints, fromTick, toTick, width, height, currentTick, currentSeconds, tickToSeconds, playheadY });
    this.keySpans.draw({
      spans: props.keySpans,
      labelOf: props.keyLabelOf,
      fromTick, toTick, width, height, currentTick, currentSeconds, tickToSeconds, playheadY,
    });

    this.workRange.draw({
      range: props.workRange ?? null,
      ruled: props.workRangeRuled ?? true,
      fromTick,
      toTick,
      width,
      height,
    });

    this.songBoundaries.draw({
      boundaries: props.songBoundaries,
      version: props.sequenceVersion,
      fromTick,
      toTick,
      width,
      height,
    });

    // Draw the past overlay: a darkening gradient covering everything below the playhead…
    this.pastOverlay.draw({ width, height: height - playheadY, y: playheadY });
    // …and what the line ate this frame leaves as glitter.
    this.noteDust.emit({
      entries,
      fromTick,
      toTick,
      height,
      playheadY,
      laneColors: props.laneColors,
      deltaMS: props.deltaMS,
    });

    // Find the active chord's root color for the playhead
    let activeChordColor: number | undefined;
    for (let i = hints.length - 1; i >= 0; i--) {
      const h = hints[i];
      if (h.chordRoot !== undefined && h.chordName && h.tick <= currentTick) {
        activeChordColor = pitchClassColor(h.chordRoot);
        break;
      }
    }

    this.nowLine.draw({ width, playheadY, playoutDelayPx: props.playoutDelayPx ?? 0 });

    this.playhead.draw({
      width,
      height,
      distanceFromBottom: height - playheadY,
      color: activeChordColor,
    });

    // this.noteHints.draw({ entries, currentTick, playheadY, fromTick, toTick, width, height });

    this.vignette.draw({ width, height });
  }

  /**
   * Cleanup all Pixi objects.
   */
  destroy(): void {
    this.pedalTrails.destroy();
    this.pedalRibbon.destroy();
    this.pedalFlash.destroy();
    this.keySpans.destroy();
    this.recordMarker.destroy();
    this.measureRuler.destroy();
    this.harmony.destroy();
    this.harmonyLines.destroy();
    this.vignette.destroy();
    this.noteHints.destroy();
    this.noteEffects.destroy();
    this.nowLine.destroy();
    this.playhead.destroy();
    this.songBoundaries.destroy();
    this.workRange.destroy();
    this.noteDust.destroy();
    this.pastOverlay.destroy();
    this.notes.destroy();
    this.measures.destroy();
    this.octaveGrid.destroy();
    this.background.destroy();
  }

  private shouldRedraw(props: PianoRollRendererProps): boolean {
    const prev = this.previousProps;
    const prevHintVersion = this.previousHintVersion;
    const prevEntryCount = this.previousEntryCount;
    // Shallow copy so we're never aliased to the caller's mutable object.
    // `entries` is the exception the copy cannot cover — see
    // `previousEntryCount`.
    this.previousProps = { ...props };
    this.previousHintVersion = props.hintSelection?.version ?? -1;
    this.previousEntryCount = props.entries.length;

    if (!prev) {
      return true;
    }

    return (
      prev.fromTick !== props.fromTick
      || prev.toTick !== props.toTick
      || prev.width !== props.width
      || prev.height !== props.height
      || prev.offsetX !== props.offsetX
      || prevEntryCount !== props.entries.length
      || prevHintVersion !== (props.hintSelection?.version ?? -1)
      || (prev.highlightActiveBeat ?? false) !== (props.highlightActiveBeat ?? false)
      || (prev.rulerExpanded ?? false) !== (props.rulerExpanded ?? false)
      || (prev.recordStartTick ?? null) !== (props.recordStartTick ?? null)
      || (prev.recordEndTick ?? null) !== (props.recordEndTick ?? null)
      || (prev.recordDragging ?? null) !== (props.recordDragging ?? null)
      || (prev.recordDiscardProgress ?? 0) !== (props.recordDiscardProgress ?? 0)
      || (prev.recordDragX ?? null) !== (props.recordDragX ?? null)
      || prev.workRange?.startTick !== props.workRange?.startTick
      || prev.workRange?.endTick !== props.workRange?.endTick
      || prev.sequenceVersion !== props.sequenceVersion
    );
  }
}
